import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, ChevronDown } from "lucide-react";
import { Link } from "wouter";

export default function HeroSection() {
  const [location, setLocation] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [bedrooms, setBedrooms] = useState("");
  const [bathrooms, setBathrooms] = useState("");
  const [priceType, setPriceType] = useState("list");
  const [selectedBounds, setSelectedBounds] = useState<{ north: number; south: number; east: number; west: number } | null>(null);
  const locationInputRef = useRef<HTMLInputElement>(null);

  // Load Google Places Autocomplete and attach to the location input
  useEffect(() => {
    const initAutocomplete = () => {
      try {
        const inputEl = locationInputRef.current as HTMLInputElement | null;
        if (!inputEl || !(window as any).google?.maps?.places) return;
        const autocomplete = new (window as any).google.maps.places.Autocomplete(inputEl, {
          fields: ["formatted_address", "geometry"],
          types: ["geocode"],
          componentRestrictions: { country: ["us"] },
        });
        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          const formatted = place?.formatted_address || inputEl.value || "";
          setLocation(formatted);
          const geometry = place?.geometry;
          if (geometry?.viewport) {
            const vp = geometry.viewport;
            try {
              const north = vp.getNorth?.() ?? vp.La?.lo ?? vp.na?.hi;
              const south = vp.getSouth?.() ?? vp.La?.hi ?? vp.na?.lo;
              const east = vp.getEast?.() ?? vp.Aa?.hi ?? vp.ia?.hi;
              const west = vp.getWest?.() ?? vp.Aa?.lo ?? vp.ia?.lo;
              if ([north, south, east, west].every((n) => Number.isFinite(n))) {
                setSelectedBounds({ north, south, east, west });
              }
            } catch {}
          } else if (geometry?.location) {
            const lat = geometry.location.lat();
            const lng = geometry.location.lng();
            const dLat = 0.05, dLng = 0.05;
            setSelectedBounds({ north: lat + dLat, south: lat - dLat, east: lng + dLng, west: lng - dLng });
          }
        });
      } catch {}
    };

    const ensureGooglePlaces = async () => {
      if (typeof window === 'undefined' || typeof document === 'undefined') return;
      if ((window as any).google?.maps?.places) {
        initAutocomplete();
        return;
      }
      if ((window as any).__googleMapsLoader) {
        await (window as any).__googleMapsLoader;
        initAutocomplete();
        return;
      }
      const apiKey = (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY;
      if (!apiKey) return; // silently skip if no key configured
      (window as any).__googleMapsLoader = new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load Google Maps JS API"));
        document.head.appendChild(script);
      });
      try {
        await (window as any).__googleMapsLoader;
        initAutocomplete();
      } catch {}
    };

    void ensureGooglePlaces();
  }, []);

  const handleSearch = () => {
    // Construct search query and navigate to search results
    const searchParams = new URLSearchParams();
    if (location) searchParams.set("city", location);
    if (minPrice && minPrice !== "none") searchParams.set("minPrice", minPrice);
    if (maxPrice && maxPrice !== "none") searchParams.set("maxPrice", maxPrice);
    if (propertyType && propertyType !== "all") searchParams.set("propertyType", propertyType);
    if (bedrooms && bedrooms !== "any") searchParams.set("bedrooms", bedrooms);
    if (selectedBounds) searchParams.set("bounds", JSON.stringify(selectedBounds));
    
    // Navigate to Buy page with search params to drive map & filters
    if (typeof window !== 'undefined') {
      window.location.href = `/buy?${searchParams.toString()}`;
    }
  };

  const formatPriceDisplay = () => {
    if (minPrice === "none" || !minPrice) {
      if (maxPrice === "none" || !maxPrice) return "Price";
    }
    
    const hasMin = minPrice && minPrice !== "none";
    const hasMax = maxPrice && maxPrice !== "none";
    
    if (hasMin && hasMax) {
      return `$${parseInt(minPrice).toLocaleString()} - $${parseInt(maxPrice).toLocaleString()}`;
    }
    if (hasMin) return `$${parseInt(minPrice).toLocaleString()}+`;
    if (hasMax) return `Under $${parseInt(maxPrice).toLocaleString()}`;
    return "Price";
  };

  const formatBedroomsBathsDisplay = () => {
    const parts = [];
    if (bedrooms && bedrooms !== "any") parts.push(`${bedrooms}+ beds`);
    if (bathrooms && bathrooms !== "any") parts.push(`${bathrooms}+ baths`);
    if (parts.length === 0) return "Beds & Baths";
    return parts.join(", ");
  };

  return (
    <section className="relative h-screen" itemScope itemType="https://schema.org/RealEstateListing">
      {/* Hero Background with SEO Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 107, 166, 0.4), rgba(0, 53, 102, 0.6)), url('https://images.unsplash.com/photo-1613490493576-7fde63acd811?ixlib=rb-4.0.3&auto=format&fit=crop&w=2071&h=1380')`
        }}
        role="img"
        aria-label="Luxury South Florida waterfront homes with ocean views, modern architecture, and premium amenities"
      >
        {/* SEO Image Tag - Hidden but crawlable */}
        <img 
          src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?ixlib=rb-4.0.3&auto=format&fit=crop&w=2071&h=1380"
          alt="Luxury South Florida waterfront real estate featuring oceanfront condos, bayfront estates, and premium coastal properties in Miami, Fort Lauderdale, and Boca Raton"
          className="sr-only"
          itemProp="image"
          loading="eager"
        />
      </div>
      
      {/* Hero Content */}
      <div className="relative z-10 flex items-center justify-center h-full">
        <div className="text-center text-white max-w-4xl px-4">
          <h1 className="font-playfair text-5xl md:text-7xl font-bold mb-6">
            South Florida <span className="text-luxury-gold">Waterfront</span> Real Estate
          </h1>
          <p className="text-xl md:text-2xl mb-12 font-light">
            Luxury waterfront homes, condos, and estates in Miami, Fort Lauderdale, and Boca Raton. Explore 100+ premium listings with our interactive map search.
          </p>
          
          {/* Property Filter Bar */}
          <div className="bg-white rounded-2xl p-4 shadow-2xl max-w-6xl mx-auto">
            <div className="flex flex-wrap gap-3 items-center">
              
              {/* Location Input */}
              <div className="flex-1 min-w-[300px]">
                <Input
                  type="text"
                  placeholder="Enter a city, neighborhood, address, or ZIP code"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="border-gray-300 focus:border-ocean-blue focus:ring-ocean-blue text-deep-navy h-12 text-base"
                  ref={locationInputRef}
                />
              </div>

              {/* Price Dropdown */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-12 px-4 border-gray-300 hover:border-ocean-blue text-deep-navy justify-between min-w-[120px]">
                    {formatPriceDisplay()}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                  <div className="p-4">
                    <Tabs value={priceType} onValueChange={setPriceType} className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="list">List Price</TabsTrigger>
                        <TabsTrigger value="monthly">Monthly Payment</TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="list" className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-2 block">Minimum</label>
                            <Select value={minPrice} onValueChange={setMinPrice}>
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="No Min" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No Min</SelectItem>
                                <SelectItem value="500000">$500K</SelectItem>
                                <SelectItem value="750000">$750K</SelectItem>
                                <SelectItem value="1000000">$1M</SelectItem>
                                <SelectItem value="2000000">$2M</SelectItem>
                                <SelectItem value="3000000">$3M</SelectItem>
                                <SelectItem value="4000000">$4M</SelectItem>
                                <SelectItem value="5000000">$5M</SelectItem>
                                <SelectItem value="10000000">$10M</SelectItem>
                                <SelectItem value="20000000">$20M</SelectItem>
                                <SelectItem value="50000000">$50M</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-2 block">Maximum</label>
                            <Select value={maxPrice} onValueChange={setMaxPrice}>
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="No Max" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No Max</SelectItem>
                                <SelectItem value="750000">$750K</SelectItem>
                                <SelectItem value="1000000">$1M</SelectItem>
                                <SelectItem value="2000000">$2M</SelectItem>
                                <SelectItem value="3000000">$3M</SelectItem>
                                <SelectItem value="4000000">$4M</SelectItem>
                                <SelectItem value="5000000">$5M</SelectItem>
                                <SelectItem value="10000000">$10M</SelectItem>
                                <SelectItem value="20000000">$20M</SelectItem>
                                <SelectItem value="50000000">$50M</SelectItem>
                                <SelectItem value="100000000">$100M+</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="monthly" className="space-y-4 mt-4">
                        <div className="text-center py-4 text-gray-500">
                          Monthly payment calculator coming soon
                        </div>
                      </TabsContent>
                    </Tabs>
                    
                    <div className="flex gap-2 mt-4">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => {
                          setMinPrice("none");
                          setMaxPrice("none");
                        }}
                      >
                        Clear
                      </Button>
                      <Button className="flex-1 bg-ocean-blue hover:bg-bright-blue">
                        Apply
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Beds & Baths Dropdown */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-12 px-4 border-gray-300 hover:border-ocean-blue text-deep-navy justify-between min-w-[140px]">
                    {formatBedroomsBathsDisplay()}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-4" align="start">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Bedrooms</label>
                      <div className="flex gap-2">
                        {["any", "1", "2", "3", "4", "5+"].map((bed) => (
                          <Button
                            key={bed}
                            variant={bedrooms === bed ? "default" : "outline"}
                            size="sm"
                            className="flex-1"
                            onClick={() => setBedrooms(bed)}
                          >
                            {bed === "any" ? "Any" : bed}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Bathrooms</label>
                      <div className="flex gap-2">
                        {["any", "1", "2", "3", "4", "5+"].map((bath) => (
                          <Button
                            key={bath}
                            variant={bathrooms === bath ? "default" : "outline"}
                            size="sm"
                            className="flex-1"
                            onClick={() => setBathrooms(bath)}
                          >
                            {bath === "any" ? "Any" : bath}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => {
                          setBedrooms("any");
                          setBathrooms("any");
                        }}
                      >
                        Clear
                      </Button>
                      <Button className="flex-1 bg-ocean-blue hover:bg-bright-blue">
                        Apply
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Home Type Dropdown */}
              <Select value={propertyType} onValueChange={setPropertyType}>
                <SelectTrigger className="border-gray-300 focus:border-ocean-blue focus:ring-ocean-blue text-deep-navy h-12 min-w-[140px]">
                  <SelectValue placeholder="Home Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Single Family">House</SelectItem>
                  <SelectItem value="Condo">Condo</SelectItem>
                  <SelectItem value="Estate">Estate</SelectItem>
                </SelectContent>
              </Select>

              {/* Search Button */}
              <Button 
                onClick={handleSearch}
                className="bg-ocean-blue text-white hover:bg-bright-blue transition-colors duration-200 font-medium h-12 px-8"
              >
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
